import { Link, useNavigate, useLocation } from "react-router-dom";
import React, { useState, useEffect, useMemo, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { IoClose } from "react-icons/io5";
import { SiMoneygram, SiTelegram } from "react-icons/si";
import { BiWallet, BiShareAlt } from "react-icons/bi";
import { IoChevronDown } from "react-icons/io5";
import config from "../config";
import Header from "./Header";
import LanguageContext from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { fetchBalance, updateUserLanguage, issueWebLoginTicket } from "../utils/api";
import { buildLanguageOptions, DEFAULT_LANGUAGE_OPTION, getLanguageStorageCode } from "../utils/languageOptions";
import { assetPath } from "../utils/assets";
import { formatCurrency } from "../utils/global";

const TELEGRAM_BOT_URL = "https://t.me/Indo368_bot";

const NavItem = ({ to, icon, label, active, labelClassName = "", onClick }) => {
    const content = (
        <>
            <div className="flex-center transition-transform duration-300">
                {icon}
            </div>
            <span className={`text-xs font-medium duration-300 mt-1 ${labelClassName}`}>
                {label}
            </span>
        </>
    );

    const activeClassName = "bg-[linear-gradient(180deg,_#00EDFF_0%,_#00617D_100%)]";
    const baseClass = `flex-1 flex-center flex-col h-full transition-all duration-300 ${
        active ? activeClassName : ""
    } text-white`;

    return onClick ? (
        <button onClick={onClick} className={baseClass} type="button">{content}</button>
    ) : (
        <Link to={to} className={baseClass}>{content}</Link>
    );
};

const FloatingMenu = () => {
    const { t, i18n } = useTranslation();
    const { token, logout, phoneNumber } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const telegramWebApp = window.Telegram?.WebApp;
    const isTelegramMiniApp = Boolean(
        telegramWebApp?.initData ||
        telegramWebApp?.initDataUnsafe?.user?.id ||
        telegramWebApp?.initDataUnsafe?.start_param
    );
    const languageContext = useContext(LanguageContext);
    const languageList = useMemo(() => languageContext?.languageList || [], [languageContext?.languageList]);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState(localStorage.getItem("language") || DEFAULT_LANGUAGE_OPTION.code);
    const [isDesktopLanguageOpen, setIsDesktopLanguageOpen] = useState(false);
    const [balance, setBalance] = useState(0);
    const [webLoginTicket, setWebLoginTicket] = useState("");

    // Prevent scrolling
    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = 'hidden';
            document.body.style.touchAction = 'none';
        } else {
            document.body.style.overflow = 'unset';
            document.body.style.touchAction = 'auto';
        }

        return () => {
            document.body.style.overflow = 'unset';
            document.body.style.touchAction = 'auto';
        };
    }, [isMenuOpen]);

    const isNavActive = (path) => location.pathname === path && !isMenuOpen;
    const ImageIcon = ({ src, alt }) => <img src={src} alt={alt} className="size-sm" />;

    const handleTelegramRedirect = useCallback(() => {
        window.location.assign(TELEGRAM_BOT_URL);
    }, []);

    const mainNavItems = useMemo(() => [
        { id: "lobby", path: "/", labelKey: "lobby", icon: assetPath("/icons/home.png"), activeIcon: assetPath("/icons/home_active.png") },
        { id: "deposit", path: "/deposit", labelKey: "Deposit", icon: assetPath("/icons/card.png"), activeIcon: assetPath("/icons/card.png") },
        { id: "promotion", path: "/promotion", labelKey: "Promotion", icon: assetPath("/icons/promotion.png"), activeIcon: assetPath("/icons/promotion_active.png") },
    ], []);

    const categories = useMemo(() => [
        { id: "wallet", icon: <BiWallet className="size-sm" />, labelKey: "wallet", path: "/wallet" },
        { id: "promotion-menu", icon: <img src={assetPath("/icons/promotion_active.png")} alt="promotion" className="size-sm" />, labelKey: "Promotion", path: "/promotion" },
        { id: "cash-back", icon: <SiMoneygram className="size-sm" />, labelKey: "cashBack", path: "/cash-back" },
        { id: "rebate", icon: <SiMoneygram className="size-sm" />, labelKey: "rebate", path: "/rebate" },
        { id: "referral", icon: <BiShareAlt className="size-sm" />, labelKey: "referral", path: "/referral" },
        { id: "about-us", icon: <img src={assetPath("/icons/aboutus.png")} alt="aboutus" className="size-sm" />, labelKey: "aboutUs", path: "/about-us" },
    ], []);

    const mobileWebCategory = useMemo(() => ({
        id: "web",
        icon: <img src={assetPath("/icons/web_active.png")} alt="web" className="size-sm" />,
        labelKey: "web",
        href: phoneNumber
            ? `https://indo368app.com/?phone=${encodeURIComponent(phoneNumber)}`
            : "https://indo368app.com/",
    }), [phoneNumber]);

    const mobileTelegramCategory = useMemo(() => ({
        id: "telegram",
        icon: <SiTelegram className="size-sm" />,
        label: "Telegram",
        href: TELEGRAM_BOT_URL,
    }), []);

    const desktopNavItems = useMemo(() => {
        const homeItem = mainNavItems.find((item) => item.id === "lobby");
        const promotionItem = mainNavItems.find((item) => item.id === "promotion");
        const walletItem = categories.find((item) => item.id === "wallet");
        const secondaryItems = categories.filter((item) => !["wallet", "promotion-menu"].includes(item.id));
        const aboutUsIndex = secondaryItems.findIndex((item) => item.id === "about-us");
        const orderedSecondaryItems = [...secondaryItems];

        if (walletItem && aboutUsIndex >= 0) {
            orderedSecondaryItems.splice(aboutUsIndex, 0, walletItem);
        }

        return [
            homeItem,
            promotionItem,
            ...orderedSecondaryItems,
        ]
            .filter(Boolean)
            .map((item) => ({ ...item, icon: null, activeIcon: null, iconNode: item.icon || null }));
    }, [categories, mainNavItems]);

    const guestAllowedPaths = useMemo(() => new Set(["/", "/about-us", "/cash-back", "/rebate", "/promotion", "/referral"]), []);
    const visibleCategories = useMemo(
        () => (token ? categories : categories.filter((item) => guestAllowedPaths.has(item.path))),
        [token, categories, guestAllowedPaths]
    );
    const visibleMobileCategories = useMemo(
        () => [...visibleCategories, isTelegramMiniApp ? mobileWebCategory : mobileTelegramCategory],
        [visibleCategories, isTelegramMiniApp, mobileWebCategory, mobileTelegramCategory]
    );
    const visibleDesktopNavItems = useMemo(
        () => (token ? desktopNavItems : desktopNavItems.filter((item) => guestAllowedPaths.has(item.path))),
        [token, desktopNavItems, guestAllowedPaths]
    );

    useEffect(() => {
        if (!token || !isTelegramMiniApp) {
            setWebLoginTicket("");
            return;
        }

        let isMounted = true;

        const refreshTicket = async () => {
            try {
                const response = await issueWebLoginTicket();
                if (!isMounted) {
                    return;
                }

                if (response?.success && response?.data) {
                    setWebLoginTicket(response.data);
                }
            } catch (error) {
                if (isMounted) {
                    setWebLoginTicket("");
                }
            }
        };

        refreshTicket();
        const intervalId = setInterval(refreshTicket, 60 * 1000);

        return () => {
            isMounted = false;
            clearInterval(intervalId);
        };
    }, [token, isTelegramMiniApp]);

    const handleWebRedirect = useCallback(async () => {
        let ticket = webLoginTicket;

        if (!ticket && token && isTelegramMiniApp) {
            try {
                const response = await issueWebLoginTicket();
                if (response?.success && response?.data) {
                    ticket = response.data;
                    setWebLoginTicket(response.data);
                }
            } catch (error) {
                ticket = "";
            }
        }

        const phoneQuery = phoneNumber ? `?phone=${encodeURIComponent(phoneNumber)}` : "";
        const targetUrl = ticket
            ? `https://indo368app.com/auth/web-login${phoneQuery}#ticket=${encodeURIComponent(ticket)}`
            : mobileWebCategory.href;

        if (window.Telegram?.WebApp?.openLink) {
            window.Telegram.WebApp.openLink(targetUrl, { try_instant_view: false });
            return;
        }

        window.open(targetUrl, "_blank", "noopener,noreferrer");
    }, [webLoginTicket, token, isTelegramMiniApp, mobileWebCategory.href, phoneNumber]);

    const mobileBottomNavItems = useMemo(() => {
        if (isTelegramMiniApp) {
            return [
                mainNavItems[0],
                mainNavItems[2],
                {
                    id: "web",
                    labelKey: "web",
                    icon: assetPath("/icons/web.png"),
                    activeIcon: assetPath("/icons/web.png"),
                    onClick: handleWebRedirect,
                },
            ];
        }

        return [
            mainNavItems[0],
            mainNavItems[2],
            {
                id: "telegram",
                label: "Telegram",
                iconNode: () => <SiTelegram className="size-sm text-black" />,
                onClick: handleTelegramRedirect,
            },
        ];
    }, [isTelegramMiniApp, mainNavItems, handleTelegramRedirect, handleWebRedirect]);

    const languages = useMemo(() => buildLanguageOptions(languageList), [languageList]);

    const currentLanguage = useMemo(() => (
        languages.find((lang) => lang.code === selectedLanguage) ||
        languages.find((lang) => lang.code === DEFAULT_LANGUAGE_OPTION.code) ||
        DEFAULT_LANGUAGE_OPTION
    ), [languages, selectedLanguage]);

    useEffect(() => {
        if (!languages.length) return;

        const storedLanguage = localStorage.getItem("language");
        const storedLanguageMatch = storedLanguage
            ? languages.find((lang) => lang.code === storedLanguage)
            : null;

        if (storedLanguageMatch) {
            if (storedLanguageMatch.code !== selectedLanguage) {
                setSelectedLanguage(storedLanguageMatch.code);
            }
            if (i18n.language !== storedLanguageMatch.i18nCode) {
                i18n.changeLanguage(storedLanguageMatch.i18nCode);
            }
            return;
        }

        const storedLanguageId = localStorage.getItem("language_id");
        if (!storedLanguageId) return;

        const match = languages.find((lang) => String(lang.id) === String(storedLanguageId));
        if (match && match.code !== selectedLanguage) {
            setSelectedLanguage(match.code);
            localStorage.setItem("language", match.code);
            i18n.changeLanguage(match.i18nCode);
        }
    }, [languages, i18n, selectedLanguage]);

    useEffect(() => {
        const storedLang = localStorage.getItem("language");
        const currentLang = getLanguageStorageCode(i18n.language);

        if (storedLang && storedLang !== selectedLanguage) {
            setSelectedLanguage(storedLang);
            return;
        }

        if (!storedLang && currentLang !== selectedLanguage) {
            setSelectedLanguage(currentLang);
        }
    }, [i18n.language, selectedLanguage]);

    useEffect(() => {
        let isMounted = true;

        const loadBalance = async () => {
            if (!token) {
                setBalance(0);
                return;
            }

            try {
                const response = await fetchBalance();
                if (!isMounted) {
                    return;
                }
                setBalance(response.success ? Number(response.data) || 0 : 0);
            } catch (error) {
                if (isMounted) {
                    setBalance(0);
                }
            }
        };

        loadBalance();
        window.addEventListener("wallet:refresh", loadBalance);

        return () => {
            isMounted = false;
            window.removeEventListener("wallet:refresh", loadBalance);
        };
    }, [token]);

    const handleDesktopLanguageSelect = async (language) => {
        setSelectedLanguage(language.code);
        localStorage.setItem("language", language.code);
        if (language?.id != null) {
            localStorage.setItem("language_id", String(language.id));
        }
        i18n.changeLanguage(language.i18nCode);
        setIsDesktopLanguageOpen(false);

        if (!token || language?.id == null) {
            return;
        }

        try {
            const response = await updateUserLanguage(language.id);
            if (!response.success) {
                console.error("Failed to update language", response.data);
            }
        } catch (error) {
            console.error("Failed to update language", error);
        }
    };

    const desktopLinkClassName = (path) => {
        const active = location.pathname === path;

        return `group relative flex items-center gap-2 px-3 py-5 text-[15px] font-medium transition-colors duration-300 ${
            active
                ? "text-button-blue"
                : "text-white/70 hover:text-white"
        }`;
    };

    return (
        <div className="relative">
            <div className="hidden lg:block fixed top-0 left-0 right-0 z-[1000]">
                <nav className="flex min-h-[94px] items-center justify-between gap-6 bg-[linear-gradient(180deg,_rgba(19,19,19,0.98)_0%,_rgba(15,15,15,0.98)_100%)] px-8 shadow-[0_24px_60px_-36px_rgba(0,0,0,0.85)]">
                    <Link to="/" className="flex h-full shrink-0 items-center border-r border-white/10 pr-10">
                        <img
                            src={assetPath("/icons/logo.png")}
                            className="w-[180px]"
                            alt="Logo"
                        />
                    </Link>

                    <div className="flex flex-1 items-center justify-start gap-3">
                    {visibleDesktopNavItems.map((item) => (
                        <React.Fragment key={item.id}>
                            <Link to={item.path} className={desktopLinkClassName(item.path)}>
                                <span>{t(item.labelKey)}</span>
                                <span className={`absolute bottom-0 left-0 right-0 h-[3px] bg-button-blue transition-opacity duration-300 ${location.pathname === item.path ? "opacity-100" : "opacity-0"}`} />
                            </Link>
                            {token && item.id === "about-us" && (
                                <button
                                    type="button"
                                    onClick={logout}
                                    className="ml-4 flex items-center justify-center rounded-full border border-white/15 bg-white/[0.03] px-5 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-white/65 transition-colors duration-300 hover:border-red-300/45 hover:bg-red-500/10 hover:text-red-100"
                                >
                                    <span>{t("logout")}</span>
                                </button>
                            )}
                        </React.Fragment>
                    ))}
                    </div>
                </nav>

                <div className="flex min-h-[68px] items-center justify-between border-t border-white/5 bg-[linear-gradient(180deg,_rgba(43,43,43,0.98)_0%,_rgba(32,32,32,0.98)_100%)] px-8 text-white/70 shadow-[0_22px_48px_-36px_rgba(0,0,0,0.85)]">
                    <div className="flex items-center gap-5 text-base">
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setIsDesktopLanguageOpen((prev) => !prev)}
                                className="flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white transition-colors duration-300 hover:bg-white/10"
                            >
                                <img
                                    src={currentLanguage.iconSrc}
                                    alt={currentLanguage.name}
                                    className="h-6 w-6 rounded-full object-cover"
                                />
                                <span>{currentLanguage.name}</span>
                                <IoChevronDown className={`text-base transition-transform duration-300 ${isDesktopLanguageOpen ? "rotate-180" : ""}`} />
                            </button>

                            <div
                                className={`absolute left-0 top-full z-[1005] mt-2 min-w-[220px] overflow-hidden rounded-2xl border border-white/10 bg-[#1b1b1be6] shadow-[0_22px_48px_-36px_rgba(0,0,0,0.85)] backdrop-blur-xl transition-all duration-200 ${
                                    isDesktopLanguageOpen ? "visible opacity-100 translate-y-0" : "invisible pointer-events-none opacity-0 -translate-y-2"
                                }`}
                            >
                                {languages.map((language) => (
                                    <button
                                        key={language.id ?? language.apiCode}
                                        type="button"
                                        onClick={() => handleDesktopLanguageSelect(language)}
                                        className={`flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition-colors duration-200 ${
                                            language.code === selectedLanguage ? "bg-white/10 text-white" : "text-white/80 hover:bg-white/5 hover:text-white"
                                        }`}
                                    >
                                        <img
                                            src={language.iconSrc}
                                            alt={language.name}
                                            className="h-6 w-6 rounded-full object-cover"
                                        />
                                        <span>{language.name}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <a
                            href={TELEGRAM_BOT_URL}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center justify-center rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-white transition-colors duration-300 hover:bg-white/10"
                        >
                            Telegram
                        </a>

                        {token && phoneNumber && (
                            <div className="flex min-w-[132px] items-center justify-center rounded-full border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white/75">
                                {phoneNumber}
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-5">
                        <div className="flex items-center gap-3">
                            {token ? (
                                <>
                                    <Link
                                        to="/wallet"
                                        className="flex min-w-[148px] items-center justify-center gap-2 rounded-full border border-white/10 bg-white/5 px-5 py-3 text-sm font-medium text-white transition-colors duration-300 hover:bg-white/10"
                                    >
                                        <span className="text-white text-sm font-semibold">
                                            {`${config.CURRENCY_ISO_CODE} ${formatCurrency(balance, config.CURRENCY_ISO_CODE)}`}
                                        </span>
                                    </Link>
                                    <Link
                                        to="/deposit"
                                        className="desktop-pill-button min-w-[136px] rounded-full bg-[linear-gradient(180deg,_#A0F8FF_0%,_#00EDFF_35%,_#00B8EE_100%)] px-6 py-3 text-center text-sm font-medium text-black transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-0.5 active:scale-[0.98]"
                                    >
                                        {t("Deposit")}
                                    </Link>
                                    <Link
                                        to="/withdraw"
                                        className="desktop-pill-button min-w-[136px] rounded-full bg-[linear-gradient(180deg,_#A0F8FF_0%,_#00EDFF_35%,_#00B8EE_100%)] px-6 py-3 text-center text-sm font-medium text-black transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-0.5 active:scale-[0.98]"
                                    >
                                        {t("Withdraw")}
                                    </Link>
                                </>
                            ) : (
                                <>
                                    <Link
                                        to="/login"
                                        className="rounded-full border border-white/25 px-6 py-3 text-center text-sm font-black text-white transition-colors duration-300 hover:bg-white/10"
                                    >
                                        {t("login")}
                                    </Link>
                                    <Link
                                        to="/register"
                                        className="rounded-full bg-[#119BFF] px-6 py-3 text-center text-sm font-black text-white transition-colors duration-300 hover:bg-[#1da5ff]"
                                    >
                                        {t("register")}
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {isMenuOpen && (
                <>
                    <div className="fixed top-0 left-0 right-0 z-[1003]">
                        <Header compact />
                    </div>
                    <div
                        className="modal-overlay"
                        style={{ top: "64px", bottom: "56px" }}
                        onClick={() => setIsMenuOpen(false)}
                    />

                    <div
                        className="fixed left-0 right-0 bottom-0 bg-secondary animate-ease z-[1002] flex flex-col"
                        style={{ bottom: "56px", top: "64px" }}
                    >
                        <div className="flex justify-end px-4 py-3">
                            <button 
                                className="cursor-pointer" 
                                onClick={() => {
                                    setIsMenuOpen(false);
                                }}
                            >
                                <IoClose className="size-lg text-color-blue" />
                            </button>
                        </div>

                        <div className="flex flex-col pb-4 overflow-y-auto">
                            {visibleMobileCategories.map((cat) => (
                                <div key={cat.id} className="relative px-4 py-2">
                                    <div
                                        className="cursor-pointer h-10 rounded-xl px-4 py-2 flex-between"
                                        onClick={() => { 
                                            setIsMenuOpen(false);
                                            if (cat.href) {
                                                if (cat.id === "telegram") {
                                                    handleTelegramRedirect();
                                                    return;
                                                }

                                                if (cat.id === "web") {
                                                    handleWebRedirect();
                                                    return;
                                                }

                                                window.open(cat.href, "_blank", "noopener,noreferrer");
                                                return;
                                            }
                                            navigate(cat.path);
                                        }}>
                                        <div className="flex-center gap-2 text-white">
                                            {cat.icon}
                                            <span>
                                                {cat.label || t(cat.labelKey)}
                                            </span>
                                        </div>
                                    </div>

                                    {(cat.id === "web" || cat.id === "telegram") && token && !isTelegramMiniApp && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                logout();
                                                setIsMenuOpen(false);
                                            }}
                                            className="mt-2 flex h-10 w-full items-center justify-center rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium uppercase tracking-[0.12em] text-white/80 transition-colors duration-300 hover:border-white/30 hover:bg-white/10 hover:text-white"
                                        >
                                            {t("logout")}
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="px-8 py-7">
                            <p className="text-xs text-color-white-opacity mb-3 uppercase">
                                {t("license")}
                            </p>
                            <div className="flex flex-wrap items-center gap-6">
                                {[1, 2, 3, 4].map(num => (
                                    <img key={num} src={assetPath(`/icons/logo${num}.png`)} alt={t("license")} className="h-6 w-auto object-contain" />
                                ))}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {isDesktopLanguageOpen && (
                <button
                    type="button"
                    aria-label={t("closeDesktopLanguageMenu")}
                    className="fixed inset-0 z-[999] hidden lg:block"
                    onClick={() => setIsDesktopLanguageOpen(false)}
                />
            )}

            <nav className="fixed bottom-0 w-full h-[56px] flex items-center overflow-hidden btn-gradient z-[1000] lg:hidden">
                {mobileBottomNavItems.map((item) => {
                    const active = item.path ? isNavActive(item.path) : false;
                    return (
                        <NavItem
                            key={item.id}
                            to={item.path}
                            onClick={item.onClick}
                            label={item.label || t(item.labelKey)}
                            active={active}
                            icon={item.iconNode ? item.iconNode(active) : <ImageIcon src={active ? item.activeIcon : item.icon} alt={item.label || t(item.labelKey)} />}
                            labelClassName={active ? "text-white font-bold" : "text-black font-medium"}
                        />
                    );
                })}
                <NavItem 
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    active={isMenuOpen}
                    label={t('menu')} 
                    labelClassName={isMenuOpen ? "text-white font-bold" : "text-black font-medium"}
                    icon={<ImageIcon src={assetPath("/icons/menu.png")} alt="Menu" />} 
                />
            </nav>
        </div>
    );
};

export default FloatingMenu;
